import classnames from "classnames";
import React, { useEffect, useRef } from "react";
import environment from "web/script/modules/environment";
import PrimaryButton from "web/react/components/buttons/primary-button/primary-button";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import { gettext } from "web/script/modules/django-i18n";
import { AllSelectedFilterInfo, StandardFilterProps } from "web/types/feed-filters";
import { UniversalFiltersData } from "web/types/universal-filters";
import { useSidebarHandler } from "web/react/components/feeds-filters/hooks/use-sidebar-handler";
import BreadcrumbPills from "./feeds-filter-breadcrumb-pills/feeds-filter-breadcrumb-pills";
import FilterPagination from "./feeds-filters-pagination/filter-pagination";
import { PaginatedItem, PaginationProps } from "./feeds-filters-pagination/pagination-state";
import styles from "./feeds-filters.css";

interface FeedFiltersProps extends PaginationProps, StandardFilterProps {
    applyState(): void;
    canApply: boolean;
    displayedFilters: UniversalFiltersData;
    isLoading: boolean;
    lazyFacetFetch(optionGroupId: string): void;
    lazyFacetIsLoading: boolean;
    onClose?: (item: PaginatedItem) => void;
    selectedFilterInfo: AllSelectedFilterInfo;
}

// Shows roughly half a pill
const PILL_PEEK_HEIGHT = 35;

function FeedFilters({
    applyState,
    canApply,
    displayedFilters,
    isLoading,
    lazyFacetFetch,
    lazyFacetIsLoading,
    onClick,
    onClose,
    onPageChange,
    paginationState,
    pendingFilterInfo,
    selectedFilterInfo,
}: FeedFiltersProps): JSX.Element {
    let scrollAreaRef = useRef<HTMLDivElement>(null);
    let pillContainerRef = useRef<HTMLDivElement>(null);

    const [isOpen] = useSidebarHandler();
    let treeStructure = environment.getFeature("uj_tree_filter_all_screens");

    useEffect(() => {
        if (!scrollAreaRef.current || !treeStructure) {
            return;
        }

        if (scrollAreaRef.current.scrollTop != 0 && isOpen) {
            scrollAreaRef.current.scrollTop = 0;
        }
    }, [isOpen, treeStructure]);

    useEffect(() => {
        if (!scrollAreaRef.current || treeStructure) {
            return;
        }

        let top = 0;
        // On any page that's not the main page, we want to show a hint of
        //  breadcrumbs but not all the breadcrumbs.
        if (paginationState.hasActivePage() && pillContainerRef.current) {
            let pillsHeight = pillContainerRef.current.clientHeight;
            if (pillsHeight > PILL_PEEK_HEIGHT) {
                top = pillsHeight - PILL_PEEK_HEIGHT;
            }
        }
        scrollAreaRef.current.scrollTop = top;
    }, [paginationState, treeStructure]);

    return (
        <div
            className={classnames(styles.container, {
                [styles.loading]: isLoading,
            })}
        >
            <div className={styles.content} ref={scrollAreaRef}>
                <div className={styles.sticky}>
                    <h3 className={styles.title}>{gettext("general.filters")}</h3>
                    <div ref={pillContainerRef}>
                        <BreadcrumbPills
                            className={styles.breadcrumbs}
                            onClick={onClick}
                            selectedFilterInfo={selectedFilterInfo}
                        />
                    </div>
                </div>
                <FilterPagination
                    allSelectedFilterInfo={selectedFilterInfo}
                    lazyFacetFetch={lazyFacetFetch}
                    lazyFacetIsLoading={lazyFacetIsLoading}
                    onClick={onClick}
                    sections={displayedFilters.filter_section_list}
                    onClose={onClose}
                    {...{ onPageChange, paginationState, pendingFilterInfo }}
                />
            </div>
            <div className={styles.applyButton}>
                <PrimaryButton onClick={applyState} fullWidth={true} disabled={!canApply}>
                    {gettext("feed.filters.apply_btn_text") /* Apply Filters */}
                    {/* // TODO: Just realised we don't have a differences count */}
                </PrimaryButton>
            </div>
            {isLoading && <LoadingSpinner />}
        </div>
    );
}

export default FeedFilters;
